import * as RadixContextMenu from "@radix-ui/react-context-menu";
import { useDrag, UserHandlers } from "@use-gesture/react";
import classNames from "classnames";
import EventEmitter from "eventemitter3";
import {
  action,
  autorun,
  comparer,
  computed,
  IComputedValue,
  observable,
  reaction,
  runInAction,
  untracked,
} from "mobx";
import { observer } from "mobx-react-lite";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { unstable_batchedUpdates } from "react-dom";
import { GUEST_DEFAULT_NAME } from "../Constants";
import { uiStateMobx } from "../SharedIframeState";
import {
  ActionType,
  CanvasLayoutPaneRenderType,
  Pane,
  PaneType,
  PaneViewMode,
  Position,
  Rect,
} from "../Types";
import {
  RadixContextMenuContent,
  RadixContextMenuItem,
} from "../ui/ContextMenu";
import { FeatherIcon } from "../ui/FeatherIcon";
import { KeyboardShortcutIndicator } from "../ui/KeyboardShortcutIndicator";
import {
  areArraysEqual,
  assert,
  generatePaneId,
  getParentByClassName,
  secondaryModifierKey,
} from "../Utils";
import {
  addSelectionToClipboard,
  getPanesTopLeft,
  normalizePanesForClipboard,
} from "./CanvasClipboard";
import { canvasDispatch } from "./CanvasDispatch";
import {
  activePaneStates,
  canvasAtom,
  canvasLayoutIdAtom,
  canvasTransformComputed,
  canvasViewport,
  canvasViewportShouldAnimateAtom,
  exposeZoomPrevViewportBox,
  focusedPaneIdAtom,
  functionPaneHighlightIdBox,
  headerHeightComputed,
  keyboardTransformPaneIdBox,
  layoutComputed,
  paneKeyboardMappingAtom,
  selectedPaneIdsAtom,
  sidebarHoverPaneIdAtom,
  transferPaneInputSourceBox,
} from "./CanvasState";
import { DragClipboardPasteStartEventEmitter } from "./ClipboardPane";
import { FocusPaneEmitter, focusPaneInDirection } from "./FocusPane";
import { ParentComlinkAPI } from "./FrameSandboxAPI";
import {
  KeyboardCommandPriority,
  registerKeyboardCommand,
  registerKeyboardCommands,
} from "./Keyboard";
import {
  dragValueAtom,
  getFocusPaneIdOnMount,
  setFocusPaneIdOnMount,
} from "./NewPane";
import { PaneComponentContext } from "./PaneComponentContext";
import { PaneEnvironmentVariable } from "./PaneEnvironmentVariable";
import { PaneEvaluateComponent } from "./PaneEvaluateComponent";
import { PaneEvaluateTemplateComponent } from "./PaneEvaluateTemplateComponent";
import { PaneFunctionComponent } from "./PaneFunctionComponent";
import { PANE_NAME_INPUT_CLASSNAME } from "./PaneHeader";
import { PaneImportComponent } from "./PaneImportComponent";
import { PaneInputWires } from "./PaneInput";
import { isCustomLayoutId } from "./PaneLayout";
import { PaneLibrary } from "./PaneLibrary";
import { PaneLLM } from "./PaneLLM";
import { paneMutator } from "./PaneMutator";
import { PaneResizers } from "./PaneResizers";
import { PaneStateType } from "./PaneStateType";
import { PaneText } from "./PaneText";
import { getPresencesForPaneId, presenceState } from "./Presence";
import { MAGIC_LAYOUT_ID } from "./SandboxConstants";
import {
  addPositions,
  addUndoDeletionToStack,
  arePanesPartOfFunctionPane,
  convertViewportPositionToCanvasOffset,
  getMinPaneWidth,
  getPaneIdFromPaneInput,
  isPaneCollapsed,
  mapClientPositionToCanvasPosition,
  movePaneToTopIfNeeded,
  PANE_CLASSNAME,
  reactionOnCondition,
  roundPositionToGrid,
} from "./SandboxUtils";
import { applicationState } from "./Sync";
import { zoomToFit } from "./ToolbarZoomButton";

const PaneSelectionOverlayContextMenu = observer(
  forwardRef(
    (
      {
        lastContextMenuPositionRef,
      }: {
        lastContextMenuPositionRef: React.MutableRefObject<Position>;
      },
      ref: React.ForwardedRef<HTMLDivElement | null>
    ) => {
      const layoutId = canvasLayoutIdAtom.value;
      return (
        <RadixContextMenuContent ref={ref}>
          <RadixContextMenuItem
            onClick={() => {
              const clipboardPanes = addSelectionToClipboard(
                canvasAtom.value.panes
              );
              if (clipboardPanes !== undefined) {
                ParentComlinkAPI.copyPanesToClipboard(clipboardPanes);
              }
            }}
          >
            copy panes
            <KeyboardShortcutIndicator>⌘c</KeyboardShortcutIndicator>
          </RadixContextMenuItem>
          {layoutId === undefined &&
          selectedPaneIdsAtom.value.some(
            (paneId) =>
              !isPaneCollapsed(
                canvasAtom.value.panes.find((p) => p.id === paneId)
              )
          ) ? (
            <RadixContextMenuItem
              onClick={() => {
                const selectedPaneIds = selectedPaneIdsAtom.value;
                for (const selectedPaneId of selectedPaneIds) {
                  paneMutator.setCollapsed(selectedPaneId, true);
                }
              }}
            >
              collapse panes
              <KeyboardShortcutIndicator>c</KeyboardShortcutIndicator>
            </RadixContextMenuItem>
          ) : null}
          {layoutId === undefined &&
          selectedPaneIdsAtom.value.some(
            (paneId) =>
              activePaneStates.get(paneId)?.viewMode.get() ===
              PaneViewMode.Collapsed
          ) ? (
            <RadixContextMenuItem
              onClick={() => {
                const selectedPaneIds = selectedPaneIdsAtom.value;
                for (const selectedPaneId of selectedPaneIds) {
                  paneMutator.setCollapsed(selectedPaneId, false);
                }
              }}
            >
              uncollapse panes
              <KeyboardShortcutIndicator>c</KeyboardShortcutIndicator>
            </RadixContextMenuItem>
          ) : null}
          {layoutId === undefined &&
          computed(() =>
            selectedPaneIdsAtom.value.every((paneId) => {
              const paneType = activePaneStates.get(paneId)?.type;
              return (
                paneType === PaneType.Evaluate ||
                paneType === PaneType.InputText
              );
            })
          ).get() ? (
            <RadixContextMenuItem
              onClick={() => {
                const selectedPaneIds = selectedPaneIdsAtom.value;
                // setTimeout needed to avoid FocusScope refocusing overlay
                // rAF didn't seem to work
                setTimeout(() => {
                  const newPaneId = generatePaneId();
                  canvasDispatch([
                    ActionType.CreateFunctionPane,
                    {
                      paneId: newPaneId,
                      position: roundPositionToGrid(
                        mapClientPositionToCanvasPosition(
                          lastContextMenuPositionRef.current
                        ),
                        1
                      ),
                      paneIds: selectedPaneIds,
                    },
                  ]);
                  runInAction(() => {
                    selectedPaneIdsAtom.value = [];
                  });
                  setFocusPaneIdOnMount(newPaneId);
                }, 10);
              }}
            >
              create function
            </RadixContextMenuItem>
          ) : null}
          {layoutId !== undefined && layoutId !== MAGIC_LAYOUT_ID ? (
            <RadixContextMenuItem
              onClick={action(() => {
                const selectedPaneIds = selectedPaneIdsAtom.value;
                for (const selectedPaneId of selectedPaneIds) {
                  canvasDispatch([
                    ActionType.RemovePaneFromLayout,
                    {
                      layoutId,
                      paneId: selectedPaneId,
                    },
                  ]);
                }
                selectedPaneIdsAtom.value = [];
                focusedPaneIdAtom.value = undefined;
              })}
            >
              hide panes
            </RadixContextMenuItem>
          ) : (
            <RadixContextMenuItem
              onClick={() => {
                const selectedPaneIds = selectedPaneIdsAtom.value;
                if (arePanesPartOfFunctionPane(selectedPaneIds)) {
                  if (
                    !confirm(
                      "Deleting the selected panes will break a connected function pane. Continue?"
                    )
                  ) {
                    return;
                  }
                }
                addUndoDeletionToStack(selectedPaneIds);
                canvasDispatch([
                  ActionType.DeletePanes,
                  { paneIds: selectedPaneIds },
                ]);
              }}
              color="red"
              className="group justify-between"
            >
              delete panes
              <KeyboardShortcutIndicator className="group-hover:text-red-100">
                &#9003;
              </KeyboardShortcutIndicator>
            </RadixContextMenuItem>
          )}
        </RadixContextMenuContent>
      );
    }
  )
);

const DragMultiplePanesEmitter = new EventEmitter();
const PaneSelectionOverlay = observer(({ paneId }: { paneId: string }) => {
  const multiDragOverlayRef = useRef<HTMLDivElement | null>(null);

  const lastContextMenuPositionRef = useRef<Position>([0, 0]);

  const bindDrag = useDrag(
    action<UserHandlers["onDrag"]>(
      ({ first, xy, last, tap, event, cancel, memo = {} }) => {
        if (tap) {
          if (!event.ctrlKey) {
            selectedPaneIdsAtom.value = [paneId];
          }
        } else if (first) {
          if (event.altKey) {
            const selectedPaneIds = selectedPaneIdsAtom.value;
            const selectedPanes = canvasAtom.value.panes.filter((p) =>
              selectedPaneIds.includes(p.id)
            );
            const clipboardPanes = normalizePanesForClipboard(
              selectedPanes,
              true
            );
            const mousePosition = roundPositionToGrid(
              convertViewportPositionToCanvasOffset(
                xy,
                canvasTransformComputed.get(),
                canvasViewport.zoom
              ),
              1
            );
            const selectedPanesTopLeft = getPanesTopLeft(selectedPanes);
            const positionOffset = addPositions(
              selectedPanesTopLeft,
              mousePosition.map((v) => -v) as Position
            );
            DragClipboardPasteStartEventEmitter.emit("mousedown", {
              clipboardSelection: {
                id: -2,
                panes: clipboardPanes.map((p) => ({
                  ...p,
                  rect: [
                    p.rect[0] + positionOffset[0],
                    p.rect[1] + positionOffset[1],
                    p.rect[2],
                    p.rect[3],
                  ],
                })),
              },
              clientPosition: xy,
              isDuplicate: true,
            });
            cancel();
            return;
          }
          memo.startXY = xy;
          memo.offset = [0, 0];
          DragMultiplePanesEmitter.emit("start");
          multiDragOverlayRef.current!.style.cursor = "grabbing";
        } else if (last) {
          unstable_batchedUpdates(() => {
            DragMultiplePanesEmitter.emit("end");
          });
          multiDragOverlayRef.current!.style.cursor = "";
          event.preventDefault();
        } else {
          const zoom = canvasViewport.zoom;
          const newOffset = roundPositionToGrid(
            [
              (xy[0] - memo.startXY[0]) / zoom,
              (xy[1] - memo.startXY[1]) / zoom,
            ],
            1
          );
          if (
            newOffset[0] !== memo.offset[0] ||
            newOffset[1] !== memo.offset[1]
          ) {
            DragMultiplePanesEmitter.emit("move", { offset: newOffset });
            memo.offset = newOffset;
          }
        }
        return memo;
      }
    ),
    {
      filterTaps: true,
    }
  );

  return (
    <RadixContextMenu.Root>
      <RadixContextMenu.Trigger
        onContextMenu={(e) => {
          lastContextMenuPositionRef.current = [e.clientX, e.clientY];
        }}
      >
        <div
          className="absolute left-0 top-0 bottom-0 right-0 z-10 cursor-grab bg-neutral-0 bg-opacity-50 rounded touch-none select-none"
          {...bindDrag()}
          ref={multiDragOverlayRef}
        />
      </RadixContextMenu.Trigger>
      <RadixContextMenu.Portal>
        <PaneSelectionOverlayContextMenu
          lastContextMenuPositionRef={lastContextMenuPositionRef}
        />
      </RadixContextMenu.Portal>
    </RadixContextMenu.Root>
  );
});

function PaneExposeZoomOverlay({ paneId }: { paneId: string }) {
  return (
    <div
      onClick={action(() => {
        zoomToFit([paneId]);
        exposeZoomPrevViewportBox.set(undefined);
      })}
      className="absolute left-0 top-0 bottom-0 right-0 z-10 bg-neutral-0 bg-opacity-25 rounded select-none hover:shadow-pane-selected"
    />
  );
}

export function scrollPaneIntoViewport(paneRect: Rect) {
  runInAction(() => {
    const zoom = canvasViewport.zoom;
    const delta = [0, 0];

    const canvasBoundLeft = canvasViewport.offset[0];
    if (paneRect[0] < canvasBoundLeft) {
      delta[0] = (Math.ceil((canvasBoundLeft - paneRect[0]) / 16) + 1) * 16;
    } else {
      const canvasBoundRight =
        canvasViewport.offset[0] + window.innerWidth / zoom;
      const rectRight = paneRect[0] + paneRect[2];
      if (rectRight > canvasBoundRight) {
        delta[0] = (Math.floor((canvasBoundRight - rectRight) / 16) - 1) * 16;
      }
    }

    const canvasBoundTop = canvasViewport.offset[1];
    if (paneRect[1] < canvasBoundTop) {
      delta[1] = (Math.ceil((canvasBoundTop - paneRect[1]) / 16) + 1) * 16;
    } else {
      const canvasBoundBottom =
        canvasViewport.offset[1] +
        (window.innerHeight - headerHeightComputed.get()) / zoom;
      const rectBottom = paneRect[1] + paneRect[3];
      if (rectBottom > canvasBoundBottom) {
        delta[1] = (Math.floor((canvasBoundBottom - rectBottom) / 16) - 1) * 16;
      }
    }

    if (delta[0] !== 0 || delta[1] !== 0) {
      canvasViewportShouldAnimateAtom.value = true;
      canvasViewport.offset = [
        canvasViewport.offset[0] - delta[0],
        canvasViewport.offset[1] - delta[1],
      ];
    }
  });
}

const PanePresences = observer(
  ({ paneId, isSelected }: { paneId: string; isSelected: boolean }) => {
    const presenceSelections = computed(
      () =>
        getPresencesForPaneId(paneId)
          .map(({ clientId }) => clientId)
          .filter((clientId) => clientId !== `${applicationState.clientId}`),
      { equals: areArraysEqual }
    ).get();
    if (presenceSelections.length === 0) {
      return null;
    }
    return (
      <div
        className="absolute -top-px -bottom-px -left-px -right-px rounded pointer-events-none border transition"
        style={{
          borderColor: `#${presenceState[presenceSelections[0]].color}${
            isSelected ? "00" : "80"
          }`,
        }}
      >
        <div className="absolute bottom-full mb-1 -left-px flex gap-1">
          {presenceSelections.map((clientId) => (
            <div
              className="bg-primary-500 text-white px-1 py-0.5 rounded-sm"
              style={{
                backgroundColor: `#${presenceState[clientId].color}`,
              }}
              key={clientId}
            >
              {presenceState[clientId].name ?? GUEST_DEFAULT_NAME}
            </div>
          ))}
        </div>
      </div>
    );
  }
);

const PaneReferencedFunctions = observer(({ paneId }: { paneId: string }) => {
  const functionPaneIds = computed(
    () => {
      return canvasAtom.value.panes
        .filter(
          (p) => p.type === PaneType.Function && p.paneIds.includes(paneId)
        )
        .map((p) => p.id);
    },
    { equals: comparer.structural }
  ).get();
  if (functionPaneIds.length === 0) {
    return null;
  }
  return (
    <div className="absolute right-0 bottom-full mb-1 flex gap-2 font-mono-natto text-2xs">
      {functionPaneIds.map((paneId) => (
        <button
          onClick={() => {
            FocusPaneEmitter.emit("focus", { paneId });
            zoomToFit([paneId]);
          }}
          className="text-neutral-400 hover:text-primary-500 italic transition"
          key={paneId}
        >
          fn
        </button>
      ))}
    </div>
  );
});

function KeyboardTransformPaneIndicator() {
  return (
    <div className="absolute top-0 bottom-0 left-0 right-0 pointer-events-none text-neutral-400">
      <FeatherIcon
        icon="arrow-left"
        size="sm"
        className="absolute right-full mr-3 top-1/2 transform -translate-y-1/2"
      />
      <FeatherIcon
        icon="arrow-right"
        size="sm"
        className="absolute left-full ml-3 top-1/2 transform -translate-y-1/2"
      />
      <FeatherIcon
        icon="arrow-up"
        size="sm"
        className="absolute bottom-full mb-3 left-1/2 transform -translate-x-1/2"
      />
      <FeatherIcon
        icon="arrow-down"
        size="sm"
        className="absolute top-full mt-3 left-1/2 transform -translate-x-1/2"
      />
    </div>
  );
}

const focusedPaneInputsComputed = computed(() => {
  const focusedPane =
    focusedPaneIdAtom.value !== undefined
      ? canvasAtom.value.panes.find((p) => p.id === focusedPaneIdAtom.value)
      : undefined;
  return focusedPane !== undefined && "inputs" in focusedPane
    ? focusedPane.inputs
    : undefined;
});

export const TransformCanvasPositionEventEmitter = new EventEmitter();
export const PaneComponent = observer(
  ({
    paneId,
    hasMaximizedPane,
    radixPortalContainerRef,
  }: {
    paneId: string;
    hasMaximizedPane: boolean;
    radixPortalContainerRef: React.MutableRefObject<HTMLDivElement | null>;
  }) => {
    const [paneComputed] = useState(() =>
      computed(() => canvasAtom.value.panes.find((p) => p.id === paneId))
    );
    const paneType = untracked(() => paneComputed.get()!.type);
    const activePaneState = activePaneStates.get(paneId)!;

    const isDragTargetHover = computed(
      () =>
        (dragValueAtom.value?.type === "reference" &&
          dragValueAtom.value.sourceInfo.type === "pane-output" &&
          dragValueAtom.value.sourceInfo.hoverPaneId === paneId) ||
        transferPaneInputSourceBox.get()?.hoverPaneOutput?.[0] === paneId
    ).get();
    const isSelected = computed(() =>
      selectedPaneIdsAtom.value.includes(paneId)
    ).get();
    const isFocused = computed(() => focusedPaneIdAtom.value === paneId).get();
    const [isSidebarHoveredComputed] = useState(() =>
      computed(() => sidebarHoverPaneIdAtom.value === paneId)
    );
    const isSidebarHovered = isSidebarHoveredComputed.get();
    const isFunctionPaneHovered = computed(() => {
      const functionPaneId = functionPaneHighlightIdBox.get();
      if (functionPaneId === undefined) {
        return false;
      }
      const functionPane = canvasAtom.value.panes.find(
        (p) => p.id === functionPaneId
      );
      if (functionPane?.type !== PaneType.Function) {
        return false;
      }
      return functionPane.paneIds.includes(paneId);
    }).get();
    const isTransformingPane = computed(
      () => keyboardTransformPaneIdBox.get() === paneId
    ).get();
    const isExposeZoom = computed(
      () => exposeZoomPrevViewportBox.get() !== undefined
    ).get();
    const [paneInputsComputed] = useState(() =>
      computed(
        () => {
          if (paneType === PaneType.LLM) {
            assert(activePaneState.type === PaneType.LLM);
            return activePaneState.paneInputsComputed.get();
          }
          const templatePaneInputs =
            activePaneState.type === PaneType.EvaluateTemplate
              ? activePaneState.paneInputs.value
              : undefined;
          if (templatePaneInputs !== undefined) {
            const pane = paneComputed.get();
            if (pane === undefined) {
              return undefined;
            }
            assert(pane.type === PaneType.EvaluateTemplate);
            return templatePaneInputs.map((paneInput) => ({
              ...paneInput,
              source: pane.templateInputSources[paneInput.id],
            }));
          }
          if (paneType !== PaneType.Evaluate) {
            return undefined;
          }
          const pane = paneComputed.get();
          if (pane === undefined) {
            return undefined;
          }
          assert(pane.type === PaneType.Evaluate);
          return pane.inputs;
        },
        { equals: comparer.structural }
      )
    );
    const inputPaneIds = computed(() => {
      const paneInputs = paneInputsComputed.get();
      return paneInputs
        ?.map((paneInput) => getPaneIdFromPaneInput(paneInput))
        ?.filter((paneId): paneId is string => paneId !== undefined);
    }).get();
    const isInputPaneFocused = computed(
      () =>
        inputPaneIds !== undefined &&
        focusedPaneIdAtom.value !== undefined &&
        inputPaneIds.includes(focusedPaneIdAtom.value)
    ).get();
    const isOutputPaneFocused = computed(() => {
      const focusedPaneInputs = focusedPaneInputsComputed.get();
      return (
        focusedPaneInputs !== undefined &&
        focusedPaneInputs.findIndex(
          (paneInput) => getPaneIdFromPaneInput(paneInput) === paneId
        ) !== -1
      );
    }).get();
    const rectComputed = activePaneState.rect;
    const isRenderingAsLayoutRunButton = computed(
      () =>
        layoutComputed.get()?.panes.find((p) => p.id === paneId)
          ?.render?.[0] === CanvasLayoutPaneRenderType.RunButton
    ).get();
    const rootRef = useRef<HTMLDivElement>(null);
    const paneElementRef = useRef<HTMLDivElement>(null);
    const [panePortalElementBox] = useState(() =>
      observable.box<HTMLElement | null>(null)
    );
    const toggleMaximize = useCallback(
      action(
        (
          action:
            | "double click header"
            | "click button"
            | "editor keyboard shortcut"
        ) => {
          const wasMaximized = uiStateMobx.maximizedPaneId === paneId;
          uiStateMobx.maximizedPaneId = wasMaximized ? undefined : paneId;
        }
      ),
      []
    );
    const [isMaximizedComputed] = useState(() =>
      computed(() => uiStateMobx.maximizedPaneId === paneId)
    );
    const isMaximized = isMaximizedComputed.get();
    const viewMode = activePaneState.viewMode.get();
    const [isHiddenComputed] = useState(() =>
      computed(() => activePaneState.viewMode.get() === PaneViewMode.Hidden)
    );
    const isHidden = isHiddenComputed.get();
    useLayoutEffect(
      () =>
        autorun(() => {
          if (isHiddenComputed.get() && !isSidebarHoveredComputed.get()) {
            return;
          }
          if (!isMaximizedComputed.get()) {
            const rect = activePaneState.rect.get();
            rootRef.current!.style.transform = `translate(${rect[0]}px, ${rect[1]}px)`;
            paneElementRef.current!.style.width = `${rect[2] + 1}px`;
            paneElementRef.current!.style.height = `${rect[3] + 1}px`;
          } else {
            rootRef.current!.style.transform = `translate(${-canvasViewport
              .offset[0]}px, ${-canvasViewport.offset[1]}px)`;
            paneElementRef.current!.style.width = "";
            paneElementRef.current!.style.height = "";
          }
        }),
      []
    );
    useEffect(
      () =>
        reactionOnCondition(
          () => isMaximizedComputed.get(),
          () =>
            registerKeyboardCommand({
              name: "close maximized pane",
              keys: ["Escape"],
              priority: KeyboardCommandPriority.High,
              handler: action(() => {
                uiStateMobx.maximizedPaneId = undefined;
              }),
            })
        ),
      []
    );
    const [focusOnMount] = useState(() => paneId === getFocusPaneIdOnMount());
    useEffect(() => {
      if (getFocusPaneIdOnMount() === paneId) {
        setFocusPaneIdOnMount(undefined);
      }
      function onFocus({
        paneId: focusPaneId,
        noScroll,
      }: {
        paneId: string;
        noScroll: boolean;
      }) {
        if (paneId === focusPaneId && focusedPaneIdAtom.value !== paneId) {
          if (!noScroll) {
            scrollPaneIntoViewport(rectComputed.get());
          }
          const paneElement = paneElementRef.current!;
          // temporarily remove hidden class so pane can be focused
          if (paneElement.classList.contains("hidden")) {
            paneElement.classList.remove("hidden");
          }
          setShowFocusRing(true);
          paneElement.focus({
            preventScroll: true,
          });
          runInAction(() => {
            // the pane might already have been the activeElement so onFocus
            // won't fire again
            focusedPaneIdAtom.value = paneId;
          });
        }
      }
      FocusPaneEmitter.addListener("focus", onFocus);
      return () => {
        FocusPaneEmitter.removeListener("focus", onFocus);
      };
    }, []);

    useEffect(() => {
      function onTransform({
        paneIds,
        offset,
      }: {
        paneIds: string[];
        offset: Position;
      }) {
        if (paneIds.includes(paneId)) {
          paneMutator.updatePosition(paneId, [
            rectComputed.get()[0] + offset[0],
            rectComputed.get()[1] + offset[1],
          ]);
        }
      }
      TransformCanvasPositionEventEmitter.addListener("transform", onTransform);
      return () => {
        TransformCanvasPositionEventEmitter.removeListener(
          "transform",
          onTransform
        );
      };
    }, []);
    const layoutId = canvasLayoutIdAtom.value;
    useEffect(() => {
      if (isSelected) {
        let startPosition: Position | undefined;
        function onStart() {
          startPosition = [rectComputed.get()[0], rectComputed.get()[1]];
        }
        function onMove({ offset }: { offset: Position }) {
          const newX = startPosition![0] + offset[0];
          const newY = startPosition![1] + offset[1];
          if (
            rectComputed.get()[0] !== newX ||
            rectComputed.get()[1] !== newY
          ) {
            paneMutator.updatePosition(paneId, [newX, newY]);
          }
        }
        function onEnd() {
          startPosition = undefined;
        }
        DragMultiplePanesEmitter.addListener("start", onStart);
        DragMultiplePanesEmitter.addListener("move", onMove);
        DragMultiplePanesEmitter.addListener("end", onEnd);
        return () => {
          DragMultiplePanesEmitter.removeListener("start", onStart);
          DragMultiplePanesEmitter.removeListener("move", onMove);
          DragMultiplePanesEmitter.removeListener("end", onEnd);
        };
      }
    }, [isSelected]);

    useEffect(() => {
      if (isHidden && focusedPaneIdAtom.value === paneId) {
        runInAction(() => {
          focusedPaneIdAtom.value = undefined;
        });
      }
    }, [isHidden]);
    const [showFocusRing, setShowFocusRing] = useState(false);
    useEffect(() => {
      if (showFocusRing) {
        const timeout = setTimeout(() => {
          setShowFocusRing(false);
        }, 300);
        return () => {
          clearTimeout(timeout);
        };
      }
    }, [showFocusRing]);

    const showPaneBodyOnCollapsedPaneBox = useMemo(
      () => observable.box(false),
      []
    );
    useEffect(
      () =>
        reaction(
          () => activePaneState.viewMode.get(),
          action((viewMode) => {
            if (
              viewMode !== PaneViewMode.Collapsed &&
              showPaneBodyOnCollapsedPaneBox.get()
            ) {
              showPaneBodyOnCollapsedPaneBox.set(false);
            }
          })
        ),
      []
    );
    const keyboardShortcutKey = computed(
      () => paneKeyboardMappingAtom.value?.[paneId]
    ).get();
    const showCollapsedPanePreviewTimeoutRef = useRef<number | undefined>(
      undefined
    );
    useEffect(
      () => () => {
        if (showCollapsedPanePreviewTimeoutRef.current !== undefined) {
          clearTimeout(showCollapsedPanePreviewTimeoutRef.current);
        }
      },
      []
    );
    useEffect(
      () =>
        reactionOnCondition(
          () => focusedPaneIdAtom.value === paneId,
          () =>
            registerKeyboardCommands([
              {
                name: "focus pane in direction",
                keys: ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"],
                handler: (key) => {
                  focusPaneInDirection(
                    canvasAtom.value.panes,
                    paneId,
                    key === "ArrowUp"
                      ? "up"
                      : key === "ArrowDown"
                      ? "down"
                      : key === "ArrowLeft"
                      ? "left"
                      : "right",
                    canvasLayoutIdAtom.value === MAGIC_LAYOUT_ID
                  );
                },
              },
              {
                name: "focus pane name",
                keys: [`Ctrl-${secondaryModifierKey}-n`],
                handler: () => {
                  const rootElement = rootRef.current;
                  const paneNameInputElement = rootElement?.querySelector(
                    `.${PANE_NAME_INPUT_CLASSNAME}`
                  );
                  if (paneNameInputElement instanceof HTMLInputElement) {
                    paneNameInputElement.focus();
                  }
                },
              },
            ])
        ),
      []
    );

    return (
      <PaneComponentContext.Provider
        value={useMemo(
          () => ({
            paneComputed,
            activePaneState,
            showPaneBodyOnCollapsedPaneBox,
          }),
          [paneComputed, activePaneState, showPaneBodyOnCollapsedPaneBox]
        )}
      >
        {!hasMaximizedPane && !isCustomLayoutId(layoutId) ? (
          <PaneInputWires
            paneId={paneId}
            paneType={paneType}
            paneInputsComputed={paneInputsComputed}
            paneComputed={paneComputed}
          />
        ) : null}
        <div
          className={classNames(
            PANE_CLASSNAME,
            isMaximized
              ? "fixed top-[5vh] left-[5vw] z-99999"
              : "absolute top-0 left-0",
            hasMaximizedPane && !isMaximized ? "pointer-events-none" : undefined
          )}
          onMouseEnter={() => {
            if (viewMode === PaneViewMode.Collapsed) {
              showCollapsedPanePreviewTimeoutRef.current = setTimeout(
                action(() => {
                  showPaneBodyOnCollapsedPaneBox.set(true);
                  showCollapsedPanePreviewTimeoutRef.current = undefined;
                }),
                150
              );
            }
          }}
          onMouseLeave={action(() => {
            if (showCollapsedPanePreviewTimeoutRef.current !== undefined) {
              clearTimeout(showCollapsedPanePreviewTimeoutRef.current);
              showCollapsedPanePreviewTimeoutRef.current = undefined;
            }
            showPaneBodyOnCollapsedPaneBox.set(false);
          })}
          style={
            !isMaximized
              ? {
                  zIndex: hasMaximizedPane
                    ? 0
                    : isSidebarHovered ||
                      isFunctionPaneHovered ||
                      (viewMode === PaneViewMode.Collapsed &&
                        showPaneBodyOnCollapsedPaneBox.get()) ||
                      isTransformingPane
                    ? 999999
                    : (isDragTargetHover || isSelected || isFocused
                        ? 99999
                        : isInputPaneFocused || isOutputPaneFocused
                        ? 9999
                        : 0) + activePaneState.zIndex.get(),
                }
              : undefined
          }
          ref={rootRef}
        >
          <div
            className={classNames(
              "bg-neutral-0 rounded flex flex-col transition-pane",
              isSidebarHovered || isFunctionPaneHovered
                ? isHidden
                  ? "shadow-pane-sidebar-hidden-hover"
                  : "shadow-pane-sidebar-hover"
                : isDragTargetHover || isSelected
                ? "shadow-pane-selected"
                : isFocused && !isMaximized
                ? "shadow-pane-focused"
                : "shadow-pane",
              isMaximized ? "h-[90vh] w-[90vw]" : undefined,
              computed(
                () =>
                  keyboardTransformPaneIdBox.get() !== undefined &&
                  keyboardTransformPaneIdBox.get() !== paneId
              ).get()
                ? "pointer-events-none opacity-50"
                : undefined,
              {
                invisible: hasMaximizedPane && !isMaximized,
                hidden: isHidden && !isFocused && !isSidebarHovered,
                "pane-focused": isFocused,
              }
            )}
            onMouseDown={() => {
              if (isSelected) {
                return;
              }
              movePaneToTopIfNeeded(paneId);
            }}
            onFocus={action(() => {
              focusedPaneIdAtom.value = paneId;
              if (!selectedPaneIdsAtom.value.some((id) => id === paneId)) {
                selectedPaneIdsAtom.value = [];
              }
            })}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                const paneElement = paneElementRef.current!;
                if (e.target !== paneElement) {
                  if (
                    e.target instanceof HTMLElement &&
                    getParentByClassName(e.target, "cm-editor") !== undefined
                  ) {
                    return;
                  }
                  paneElement.focus();
                }
              }
              if (
                e.key === "Enter" &&
                !e.ctrlKey &&
                !e.metaKey &&
                !e.shiftKey
              ) {
                if (activePaneState.viewMode.get() !== PaneViewMode.Normal) {
                  return;
                }
                const paneElement = paneElementRef.current!;
                if (e.target === paneElement) {
                  switch (paneType) {
                    case PaneType.Evaluate:
                    case PaneType.EvaluateGlobal:
                    case PaneType.State:
                    case PaneType.InputText: {
                      const editorElement =
                        paneElement.querySelector("[contenteditable]");
                      if (editorElement !== null) {
                        // rAF needed to avoid codemirror handling enter key as well
                        requestAnimationFrame(() => {
                          (editorElement as HTMLDivElement).focus();
                        });
                      }
                      break;
                    }
                    case PaneType.Import: {
                      const inputElement =
                        paneElement.querySelector("input[type=text]");
                      if (inputElement !== null) {
                        requestAnimationFrame(() => {
                          (inputElement as HTMLInputElement).focus();
                        });
                      }
                      break;
                    }
                  }
                }
              }
            }}
            tabIndex={-1}
            ref={paneElementRef}
          >
            {paneType === PaneType.Evaluate ||
            paneType === PaneType.EvaluateGlobal ? (
              <PaneEvaluateComponent
                paneId={paneId}
                paneType={paneType}
                paneComputed={
                  paneComputed as IComputedValue<
                    Extract<Pane, { type: PaneType.Evaluate }> | undefined
                  >
                }
                panePortalElementBox={panePortalElementBox}
                radixPortalContainerRef={radixPortalContainerRef}
                isMaximized={isMaximized}
                isRenderingAsLayoutRunButton={isRenderingAsLayoutRunButton}
                toggleMaximize={toggleMaximize}
                focusOnMount={focusOnMount}
                layoutId={layoutId}
              />
            ) : paneType === PaneType.EvaluateTemplate ? (
              <PaneEvaluateTemplateComponent
                paneId={paneId}
                paneComputed={
                  paneComputed as IComputedValue<
                    | Extract<Pane, { type: PaneType.EvaluateTemplate }>
                    | undefined
                  >
                }
                virtualPaneInputsComputed={paneInputsComputed}
                isMaximized={isMaximized}
                focusOnMount={focusOnMount}
                layoutId={layoutId}
              />
            ) : paneType === PaneType.Import ? (
              <PaneImportComponent
                paneId={paneId}
                paneComputed={
                  paneComputed as IComputedValue<
                    Extract<Pane, { type: PaneType.Import }> | undefined
                  >
                }
                isMaximized={isMaximized}
                focusOnMount={focusOnMount}
              />
            ) : paneType === PaneType.InputText ? (
              <PaneText
                paneId={paneId}
                paneComputed={
                  paneComputed as IComputedValue<
                    Extract<Pane, { type: PaneType.InputText }> | undefined
                  >
                }
                isMaximized={isMaximized}
                focusOnMount={focusOnMount}
              />
            ) : paneType === PaneType.State ? (
              <PaneStateType
                paneId={paneId}
                paneComputed={
                  paneComputed as IComputedValue<
                    Extract<Pane, { type: PaneType.State }> | undefined
                  >
                }
                isMaximized={isMaximized}
                toggleMaximize={toggleMaximize}
              />
            ) : paneType === PaneType.EnvironmentVariable ? (
              <PaneEnvironmentVariable
                paneId={paneId}
                paneComputed={
                  paneComputed as IComputedValue<
                    | Extract<Pane, { type: PaneType.EnvironmentVariable }>
                    | undefined
                  >
                }
                focusOnMount={focusOnMount}
                isMaximized={isMaximized}
              />
            ) : paneType === PaneType.Library ? (
              <PaneLibrary
                paneId={paneId}
                paneComputed={
                  paneComputed as IComputedValue<
                    Extract<Pane, { type: PaneType.Library }> | undefined
                  >
                }
                focusOnMount={focusOnMount}
                isMaximized={isMaximized}
              />
            ) : paneType === PaneType.LLM ? (
              <PaneLLM
                paneId={paneId}
                paneComputed={
                  paneComputed as IComputedValue<
                    Extract<Pane, { type: PaneType.LLM }> | undefined
                  >
                }
                focusOnMount={focusOnMount}
                isMaximized={isMaximized}
              />
            ) : paneType === PaneType.Function ? (
              <PaneFunctionComponent
                paneId={paneId}
                paneComputed={
                  paneComputed as IComputedValue<
                    Extract<Pane, { type: PaneType.Function }> | undefined
                  >
                }
                focusOnMount={focusOnMount}
                isMaximized={isMaximized}
              />
            ) : null}
            {!isRenderingAsLayoutRunButton &&
            viewMode === PaneViewMode.Normal &&
            !isMaximized ? (
              <PaneResizers
                shouldRoundToGrid={true}
                rectComputed={rectComputed}
                constrainSize={([width, height]) => {
                  const newWidth = Math.max(width, getMinPaneWidth(paneType));
                  const newHeight = Math.max(
                    height,
                    activePaneState.type === PaneType.InputText
                      ? 64
                      : activePaneState.type === PaneType.Evaluate ||
                        activePaneState.type === PaneType.EvaluateGlobal
                      ? (activePaneState.editorHeight?.get() ?? 32) + 32
                      : activePaneState.type === PaneType.State
                      ? (activePaneState.editorHeight?.get() ?? 48) + 64
                      : activePaneState.type === PaneType.Import ||
                        activePaneState.type === PaneType.EnvironmentVariable
                      ? 96
                      : 128,
                    64
                  );
                  return [newWidth, newHeight];
                }}
                onUpdate={(rect) => {
                  const paneRect = rectComputed.get();
                  if (!areArraysEqual(paneRect, rect)) {
                    paneMutator.updateRect(paneId, rect);
                  }
                }}
              />
            ) : null}
            <PanePresences paneId={paneId} isSelected={isSelected} />
            <PaneReferencedFunctions paneId={paneId} />
            {isSelected && layoutId !== MAGIC_LAYOUT_ID ? (
              <PaneSelectionOverlay paneId={paneId} />
            ) : null}
            {isExposeZoom ? <PaneExposeZoomOverlay paneId={paneId} /> : null}
            <div
              ref={useCallback((element: HTMLDivElement | null) => {
                runInAction(() => {
                  panePortalElementBox.set(element);
                });
              }, [])}
            />
          </div>
          <div
            className={classNames(
              "absolute -top-px -left-px -right-px -bottom-px pointer-events-none border border-accent-300 rounded z-1",
              showFocusRing ? "opacity-100" : "opacity-0 transition"
            )}
          />
          {isTransformingPane ? <KeyboardTransformPaneIndicator /> : null}
        </div>
        {keyboardShortcutKey !== undefined ? (
          <div
            className="absolute rounded-sm h-4 flex items-center px-1 bg-primary-500 text-neutral-0 font-mono-natto z-999999"
            style={{
              top: `${rectComputed.get()[1] + 4}px`,
              left: `${rectComputed.get()[0] + 4}px`,
              transform: `scale(${1 / canvasViewport.zoom})`,
              transformOrigin: "top left",
            }}
          >
            {keyboardShortcutKey}
          </div>
        ) : null}
      </PaneComponentContext.Provider>
    );
  }
);
